import { useCallback, useEffect, useMemo, useState } from "react";
import { GripVertical, Search, UserRound, UserX, Users } from "lucide-react";

import { ApiError, api } from "../../api/client";

/**
 * Admin → Teams: who reports to whom, at a glance, with the friendliest
 * possible way to change it — drag a person onto their new manager's card,
 * or press Move and pick from a list. Every change goes through
 * PUT /api/team/manager, which blocks self-reporting and reporting loops
 * and writes to the employee's activity timeline.
 */

interface Person {
  id: number;
  name: string;
  employee_number: string;
  job_title: string | null;
  site_id: number | null;
  site_name: string | null;
  department_name: string | null;
  manager_id: number | null;
  /** Set only when the manager exists but is outside the current scope
   *  (e.g. another hub) — lets us still label their group. */
  manager_name: string | null;
  photo_path: string | null;
  is_admin: boolean;
}

const NO_MANAGER = -1;

export default function AdminTeamsPage() {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [dragId, setDragId] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<number | null>(null);
  const [pendingMove, setPendingMove] = useState<{ person: Person; newManager: Person | null } | null>(null);
  const [movePickerFor, setMovePickerFor] = useState<Person | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ employees: Person[] }>("/api/team/structure");
      setPeople(data.employees);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load teams");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const sites = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of people) if (p.site_id && p.site_name) m.set(p.site_id, p.site_name);
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [people]);

  const q = search.trim().toLowerCase();
  const matches = useCallback(
    (p: Person) =>
      (!q || p.name.toLowerCase().includes(q) || (p.job_title ?? "").toLowerCase().includes(q)) &&
      (!siteFilter || String(p.site_id ?? "") === siteFilter),
    [q, siteFilter],
  );

  // Group reports under each manager. A "team" exists for anyone with at
  // least one report; people with no manager form their own group.
  const groups = useMemo(() => {
    const reportsByManager = new Map<number, Person[]>();
    for (const p of people) {
      const key = p.manager_id ?? NO_MANAGER;
      reportsByManager.set(key, [...(reportsByManager.get(key) ?? []), p]);
    }
    const managerIds = Array.from(reportsByManager.keys()).filter((id) => id !== NO_MANAGER);
    const teams = managerIds
      .map((id) => {
        const reports = reportsByManager.get(id) ?? [];
        const manager = byId.get(id) ?? null;
        return {
          manager,
          managerId: id,
          reports,
          // Manager exists but is outside the current hub/scope.
          externalName: manager ? null : (reports[0]?.manager_name ?? null),
        };
      })
      .sort((a, b) =>
        (a.manager?.name ?? a.externalName ?? "").localeCompare(b.manager?.name ?? b.externalName ?? ""),
      );
    const unassigned = (reportsByManager.get(NO_MANAGER) ?? []).filter(
      // A top-of-tree manager with reports isn't "unassigned" in any useful
      // sense — they appear as a team card already. Only leaf people with no
      // manager need surfacing.
      (p) => !reportsByManager.has(p.id),
    );
    return { teams, unassigned };
  }, [people, byId]);

  // When searching, only show groups containing a hit (manager or report).
  const visibleTeams = groups.teams.filter(
    (g) => (!q && !siteFilter) || (g.manager ? matches(g.manager) : false) || g.reports.some(matches),
  );
  const visibleUnassigned = groups.unassigned.filter((p) => (!q && !siteFilter) || matches(p));

  const requestMove = (person: Person, newManagerId: number | null) => {
    if (newManagerId === person.id) return;
    if (newManagerId === person.manager_id) return;
    setPendingMove({ person, newManager: newManagerId == null ? null : (byId.get(newManagerId) ?? null) });
  };

  const confirmMove = async () => {
    if (!pendingMove) return;
    const { person, newManager } = pendingMove;
    try {
      await api.put("/api/team/manager", {
        employee_id: person.id,
        manager_id: newManager?.id ?? null,
      });
      setPendingMove(null);
      setToast(
        newManager
          ? `${person.name} now reports to ${newManager.name}`
          : `${person.name} no longer has a manager`,
      );
      setTimeout(() => setToast(null), 3500);
      void load();
    } catch (err) {
      setPendingMove(null);
      alert(err instanceof ApiError ? err.message : "Failed to move");
    }
  };

  const onDropOnManager = (managerId: number | null) => {
    if (dragId == null) return;
    const person = byId.get(dragId);
    setDragId(null);
    setDropTarget(null);
    if (person) requestMove(person, managerId);
  };

  if (loading) return <p className="text-sm text-gray-400">Loading teams…</p>;
  if (error) return <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold text-gray-900">Teams</h1>
        <p className="text-xs text-gray-500">
          Drag a person onto their new manager's card, or press Move. Changes apply immediately and are logged.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} strokeWidth={2} aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find a person…"
            className="w-56 rounded-lg border border-gray-300 py-1.5 pl-8 pr-3 text-sm"
          />
        </div>
        <select
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
        >
          <option value="">All sites</option>
          {sites.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </div>

      {visibleUnassigned.length > 0 && (
        <TeamCard
          title="No manager yet"
          subtitle="These people have nobody to approve their leave or run their reviews"
          icon={<UserX size={16} strokeWidth={1.75} aria-hidden />}
          people={visibleUnassigned}
          highlight={dropTarget === NO_MANAGER}
          warning
          onDragOver={(e) => { e.preventDefault(); setDropTarget(NO_MANAGER); }}
          onDragLeave={() => setDropTarget((t) => (t === NO_MANAGER ? null : t))}
          onDrop={() => onDropOnManager(null)}
          dragId={dragId}
          setDragId={setDragId}
          onMove={setMovePickerFor}
          matches={matches}
          filtered={!!(q || siteFilter)}
        />
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {visibleTeams.map((g) => (
          <TeamCard
            key={g.managerId}
            title={g.manager ? g.manager.name : (g.externalName ?? "Unknown manager")}
            subtitle={
              g.manager
                ? [g.manager.job_title, g.manager.site_name].filter(Boolean).join(" · ")
                : "Manager is outside this hub — switch to All hubs to manage their whole team"
            }
            photoPath={g.manager?.photo_path}
            count={g.reports.length}
            people={g.reports}
            highlight={dropTarget === g.managerId}
            disabled={dragId === g.managerId}
            onDragOver={(e) => {
              if (dragId === g.managerId) return;
              e.preventDefault();
              setDropTarget(g.managerId);
            }}
            onDragLeave={() => setDropTarget((t) => (t === g.managerId ? null : t))}
            onDrop={() => onDropOnManager(g.managerId)}
            dragId={dragId}
            setDragId={setDragId}
            onMove={setMovePickerFor}
            matches={matches}
            filtered={!!(q || siteFilter)}
          />
        ))}
      </div>
      {visibleTeams.length === 0 && visibleUnassigned.length === 0 && (
        <p className="text-sm text-gray-400">No teams match your search.</p>
      )}

      {pendingMove && (
        <ConfirmMoveModal
          person={pendingMove.person}
          newManager={pendingMove.newManager}
          oldManagerName={pendingMove.person.manager_id ? byId.get(pendingMove.person.manager_id)?.name ?? null : null}
          onCancel={() => setPendingMove(null)}
          onConfirm={() => void confirmMove()}
        />
      )}

      {movePickerFor && (
        <MovePickerModal
          person={movePickerFor}
          people={people}
          onClose={() => setMovePickerFor(null)}
          onPick={(managerId) => {
            setMovePickerFor(null);
            requestMove(movePickerFor, managerId);
          }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

function TeamCard({
  title,
  subtitle,
  photoPath,
  icon,
  count,
  people,
  highlight,
  disabled,
  warning,
  onDragOver,
  onDragLeave,
  onDrop,
  dragId,
  setDragId,
  onMove,
  matches,
  filtered,
}: {
  title: string;
  subtitle?: string;
  photoPath?: string | null;
  icon?: React.ReactNode;
  count?: number;
  people: Person[];
  highlight: boolean;
  disabled?: boolean;
  warning?: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: () => void;
  dragId: number | null;
  setDragId: (id: number | null) => void;
  onMove: (p: Person) => void;
  matches: (p: Person) => boolean;
  filtered: boolean;
}) {
  // When a filter is on, dim non-matching rows instead of hiding them, so the
  // team keeps its shape and you can still drop into it.
  return (
    <section
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
      className={[
        "rounded-xl border bg-white p-4 shadow-sm transition-colors",
        highlight ? "border-pme-blue ring-2 ring-pme-blue/30 bg-blue-50/40" : warning ? "border-amber-200" : "border-gray-200",
        disabled ? "opacity-60" : "",
      ].join(" ")}
    >
      <div className="flex items-center gap-3">
        {photoPath ? (
          <img src={photoPath} alt="" className="h-9 w-9 shrink-0 rounded-full object-cover" />
        ) : (
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${warning ? "bg-amber-50 text-amber-700" : "bg-pme-navy text-white"}`}>
            {icon ?? <Users size={16} strokeWidth={1.75} aria-hidden />}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{title}</p>
          {subtitle && <p className="truncate text-xs text-gray-500">{subtitle}</p>}
        </div>
        {count != null && (
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
            {count} {count === 1 ? "report" : "reports"}
          </span>
        )}
      </div>

      <ul className="mt-3 space-y-1">
        {people.map((p) => {
          const dim = filtered && !matches(p);
          return (
            <li
              key={p.id}
              draggable
              onDragStart={(e) => {
                setDragId(p.id);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={() => setDragId(null)}
              className={[
                "flex items-center gap-2 rounded-lg border border-transparent px-2 py-1.5 hover:border-gray-200 hover:bg-gray-50",
                dragId === p.id ? "opacity-40" : "",
                dim ? "opacity-35" : "",
              ].join(" ")}
            >
              <GripVertical size={14} strokeWidth={1.75} aria-hidden className="shrink-0 cursor-grab text-gray-300" />
              {p.photo_path ? (
                <img src={p.photo_path} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
              ) : (
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-pme-sky text-pme-navy">
                  <UserRound size={13} strokeWidth={1.75} aria-hidden />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-gray-900">{p.name}</p>
                <p className="truncate text-[11px] text-gray-500">
                  {[p.job_title, p.department_name].filter(Boolean).join(" · ")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onMove(p)}
                className="shrink-0 rounded-lg border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-100"
              >
                Move
              </button>
            </li>
          );
        })}
        {people.length === 0 && <li className="px-2 py-1.5 text-xs text-gray-400">No direct reports.</li>}
      </ul>
    </section>
  );
}

function ConfirmMoveModal({
  person,
  newManager,
  oldManagerName,
  onCancel,
  onConfirm,
}: {
  person: Person;
  newManager: Person | null;
  oldManagerName: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-gray-900">
          {newManager ? `Move ${person.name} to ${newManager.name}'s team?` : `Remove ${person.name}'s manager?`}
        </h3>
        <p className="mt-2 text-xs text-gray-500">
          {oldManagerName ? `Currently reports to ${oldManagerName}. ` : "Currently has no manager. "}
          {newManager
            ? `${newManager.name} will approve their leave and run their reviews from now on.`
            : "Nobody will be able to approve their leave or run their reviews until a new manager is set."}
        </p>
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={onCancel} className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} className="flex-1 rounded-lg bg-pme-blue px-3 py-2 text-sm font-semibold text-white hover:bg-pme-blue/90">
            {newManager ? "Move" : "Remove manager"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MovePickerModal({
  person,
  people,
  onClose,
  onPick,
}: {
  person: Person;
  people: Person[];
  onClose: () => void;
  onPick: (managerId: number | null) => void;
}) {
  const [query, setQuery] = useState("");
  const ql = query.trim().toLowerCase();
  const options = people
    .filter((p) => p.id !== person.id)
    .filter((p) => !ql || p.name.toLowerCase().includes(ql) || (p.job_title ?? "").toLowerCase().includes(ql))
    .slice(0, 30);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="flex max-h-[70vh] w-full max-w-sm flex-col rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-gray-900">Who should {person.name} report to?</h3>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or job title…"
          autoFocus
          className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        <ul className="mt-2 flex-1 space-y-0.5 overflow-y-auto">
          {person.manager_id != null && (
            <li>
              <button
                type="button"
                onClick={() => onPick(null)}
                className="w-full rounded-lg px-2.5 py-2 text-left text-sm text-red-600 hover:bg-red-50"
              >
                No manager
              </button>
            </li>
          )}
          {options.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onPick(p.id)}
                className="w-full rounded-lg px-2.5 py-2 text-left hover:bg-gray-50"
              >
                <span className="block text-sm font-medium text-gray-900">
                  {p.name}
                  {p.id === person.manager_id && <span className="ml-1.5 text-[11px] font-normal text-gray-400">(current)</span>}
                </span>
                <span className="block text-[11px] text-gray-500">
                  {[p.job_title, p.site_name].filter(Boolean).join(" · ")}
                </span>
              </button>
            </li>
          ))}
          {options.length === 0 && <li className="px-2.5 py-2 text-xs text-gray-400">No one found.</li>}
        </ul>
        <button type="button" onClick={onClose} className="mt-3 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600">
          Cancel
        </button>
      </div>
    </div>
  );
}
